<template>
  <div>
    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-5">{{ activityTitle }}</heading>
      <p class="mb-5">
        Set up an obstacle course, then have a volunteer put on earmuffs and eye shades and tie one
        arm behind their back (or they can just hold it there). Then safely guide them through the
        course.
      </p>
      <p class="mb-5">
        It can be quite challenging to communicate and assist patients with impaired senses and/or
        with injuries. It takes critical thinking skills and empathy to do this well. Of course, as
        a nurse in a real-life scenario, you'll also need to follow any additional procedures
        associated with the person's illness, impairments or injury.
      </p>
      <p class="mb-5">
        For the hearing impaired, you may need to eliminate background noise (like turn down the TV)
        when giving instructions.
      </p>
      <p class="mb-5">
        For the vision impaired, you may need to verify (ask them) if they can see what to do or,
        what you can do to help them. Make sure the room is well lit and that they have their
        glasses/visual aids.
      </p>
      <p class="mb-5">
        With injured or immobile patients, you may need to demonstrate how to do certain activities.
        You will also need to evaluate their strength.
      </p>
      <p class="mb-5">
        Remember, too, not to do everything for the patient. You are there to assist them. Patients
        still need their independence as much as safety allows, and to learn to do things for
        themselves as best they can.
      </p>
      <p class="mb-5">
        If your patient can’t do or understand something, try a different approach.
      </p>

      <div class="vx-row mt-10">
        <div class="vx-col lg:w-1/2 md:w-1/2 w-full mb-base">
          <p class="mb-5">Be sure to:</p>
          <ul class="default-list">
            <li>Speak clearly and slowly</li>
            <li>Reassure your patient</li>
            <li>Listen to your patient</li>
            <li>Don’t rush your patient</li>
          </ul>

          <p class="mb-5">
            You can be somewhat creative here, but the below can give you a few ideas on how to go
            about this task. The video on the right also gives you an idea about what you could do.
          </p>
        </div>
        <div class="vx-col lg:w-1/2 md:w-1/2 w-full mb-base pl-10">
          <video-trigger
            :imageSrc="videoTriggerImg1"
            button_id="guide_someone"
            class="mb-5"
            @click="showVideoPopup(popupVideo, 'guide_someone')"
          ></video-trigger>
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col lg:w-1/2 md:w-1/2 w-full mb-base">
          <p class="mb-5">Guide your patient with things like:</p>

          <ul class="default-list">
            <li>
              sitting on the lounge then getting to the dinner table <br /><u>For example:</u> make
              sure they sit in a chair they can get in and out of.
            </li>

            <li>
              helping them identify salt from pepper or vitamin jars containing something like
              tic-tacs/lollies (as an alternative to medications) <br /><u>For example:</u> note
              where things are kept on the table. Remember to use their perspective of right and
              left, not yours.
            </li>

            <li>
              going from the dinner table to wash their face and hands <br /><u>For example:</u> You
              may need to remind them that the hot tap is on the left. Make sure they have non-slip
              socks or shoes on.
            </li>

            <li>
              Putting on a dressing gown <br /><u>For example:</u> any clothing is a challenge with
              the use of one arm, so you may choose to simply assist your patient or come up with a
              safe and creative way for the patient to do this for themselves. See how Megan does it
              in the video on the right:
            </li>
          </ul>
        </div>
        <div class="vx-col lg:w-1/2 md:w-1/2 w-full mb-base pl-10">
          <video-trigger
            :imageSrc="videoTriggerImg2"
            button_id="dress_someone"
            @click="
              showVideoPopup(
                'https://www.youtube.com/embed/U04YwQcPSgY?autoplay=1',
                'dress_someone',
              )
            "
          ></video-trigger>
        </div>
      </div>
      <p class="mb-5">
        <span class="text-bold text-underline">Please Note:</span> Do not use a volunteer who has a
        real injury, impairment, or illness. This task is to be done inside and is not to involve
        stairs or heights. Use safe, soft obstacles (like cushions). We do not want to cause an
        injury, especially when this task is partly about learning to avoid injury.
      </p>

      <heading size="h3" style-size="h5" class="text-underline mb-5">You will need:</heading>

      <ul class="default-list">
        <li>A volunteer to be your patient</li>
        <li>A volunteer to take photos</li>
        <li>Eye shades (dark sunglasses)</li>
        <li>Earmuffs or ear plugs</li>
        <li>
          Belt (optional) long enough to do up around your volunteer's middle with one arm tucked in
        </li>
        <li>To upload 3 photos or 1 video of you doing this activity</li>
      </ul>

      <p class="mb-8 text-bold">
        Now answer the questions below (and upload your photos or video):
      </p>

      <label>
        1. Describe how you found this task and what you found most challenging.
        <textarea-block v-model="data.question1" :editable="isEditable" />
      </label>

      <label>
        2. What did you learn from doing this activity?
        <textarea-block v-model="data.question2" :editable="isEditable" />
      </label>

      <label>
        3. Was it what you expected?
        <switch-block v-model="data.question3" :editable="isEditable" />
      </label>

      <label>
        4. Did your volunteer feel you did a good job?
        <switch-block v-model="data.question4" :editable="isEditable" />
      </label>

      <label>
        5. Upload your photos or video:<br />
        <div v-for="index in 3" :key="index">
          <fileupload-button-immediate
            v-model="uploaded_files['uploaded_file_' + index]"
            :is-editable="isEditable"
            filetype="media"
            :index="index"
          />
        </div>
      </label>
      <save-buttons :is-editable="isEditable" @save="save" />
    </vx-card>
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      login_type: localStorage.getItem('login_type'),
      videoTriggerImg1: require('@/assets/images/experiences/nurse/obstacle_course.jpg'),
      videoTriggerImg2: require('@/assets/images/experiences/nurse/megan_dressed.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
      popupVideo:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL + '/content/nurse/obstacle_course_900.mp4',
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
